import * as React from 'react';
import styled from 'styled-components';
import Modal, {ModalVariant} from 'components/Modal';
import InputSearch from 'components/InputSearch';
import Student from './components/Student';
import {ErrorObject} from 'types';
import scrollStyles from "helpers/css/scroll";
import {CourseObject, getCourseStudentList} from "api/course";
import {getImageByPreview} from "api/library";
import {StudentObject} from "api/student/types";
import validateResponse from "helpers/validate-response";
import isError from "helpers/isError";
import {IconLoading} from "icons";

export interface ModalCourseStudentsProps {
  course: CourseObject;
  close?: () => void;
}

const Header = styled.div`
  font-size: 22px;
  font-weight: 600;
  line-height: 29px;
`;

const Content = styled.section`
  padding: 10px;
  margin-bottom: 24px;
  border-radius: 5px;
  background-color: ${({theme}) => theme.variables.backgroundColor};
  font-size: 13px;
  line-height: 17;
`;

const Course = styled.div`
  display: flex;
  align-items: center;
  border-radius: 5px;
  color: white;
`;

const CoursePreview = styled.img`
  height: 60px;
  width: 106px;
  border-radius: 5px;
  margin-right: 12px;
  object-fit: cover;
  overflow: hidden;
`;

const CourseTitle = styled.div`
  font-size: 15px;
  line-height: 19px;
`;

const NoDataBox = styled.div`
  height: 360px;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StudentsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  align-content: flex-start;
  width: calc(100% + 16px);
  margin-top: 20px;
  margin-bottom: 41px;
  min-height: 360px;
  overflow-y: auto;
  
  ${scrollStyles()}
`;

/**
 * The course students component
 * @param {ModalCourseStudents} props
 * @return {React.ReactElement}
 * @constructor
 */
export default function ModalCourseStudents(
    props: ModalCourseStudentsProps,
): React.ReactElement {
  const {course} = props;

  const [isLoading, setLoading] = React.useState(true);
  const [students, setStudents] = React.useState<StudentObject[]>([]);
  const [filteredList, setFilteredList] = React.useState<StudentObject[]>([]);
  const [searchValue, setSearchValue] = React.useState('');

  /**
   * the function checks if the student matches the search string
   * @return {React.ReactElement}
   */
  function checkStudentMatchesBySearch(student: StudentObject) {
    const targetLower = searchValue.toLowerCase();
    const studentLower = student.name.toLowerCase();

    return studentLower.includes(targetLower)
  }

  /**
   * set content data
   */
  React.useEffect(() => {
    setFilteredList(
      students.filter(checkStudentMatchesBySearch)
    )
  }, [students, searchValue])

  /**
   * the fetch data
   */
  React.useEffect(() => {
    async function fetchCourseStudents() {
      return await getCourseStudentList(props.course.id)
    }

    /**
     * the function set course students to component state
     */
    function setStudentsToState(resp: StudentObject[] | ErrorObject | any) {
      validateResponse(resp);

      if (resp && !isError(resp)) {
        setStudents(resp)
      }
    }

    fetchCourseStudents()
      .then(setStudentsToState)
      .then(() => setLoading(false))
  }, [course])


  /**
   * The function render course
   * @return {React.ReactElement}
   */
  function renderCourse() {
    return (
      <Course>
        <CoursePreview
          src={getImageByPreview(props.course.preview)}
          alt={'изображение курса'}
        />
        <CourseTitle>
          {course.title}
        </CourseTitle>
      </Course>
    );
  }

  /**
   * The function render course students or no data component
   * @return {React.ReactElement}
   */
  function renderStudents() {
    if (isLoading) {
      return (
        <NoDataBox>
          <IconLoading />
        </NoDataBox>
      )
    }

    if (!filteredList.length && !(searchValue)) {
      return (
        <NoDataBox>
          Список пуст...
        </NoDataBox>
      )
    }

    if (!filteredList.length && searchValue) {
      return (
        <NoDataBox>
          {`Студент "${searchValue}" не найден`}
        </NoDataBox>
      )
    }

    return filteredList.map(renderStudentItem);
  }

  /**
   * The function render course student
   * @return {React.ReactElement}
   */
  function renderStudentItem(student: StudentObject) {
    return (
      <Student
        key={student.intId}
        id={student._id}
        name={student.name}
        avatar={getImageByPreview(student.avatar)}
      />
    )
  }

  return (
    <Modal
      renderHeader={<Header>Студенты курса</Header>}
      variant={ModalVariant.Accent}
      onBlur={props.close}
    >
      <Content>
        {renderCourse()}
      </Content>
      <InputSearch
        placeholder="Поиск по имени"
        onChange={(e) => setSearchValue(e.target.value.trim())}
      />
      <StudentsList>
        {renderStudents()}
      </StudentsList>
    </Modal>
  );
}
